function isHistoricalRole(role) {
  return ["COMPARATOR_EOB", "COMPARATOR_AWARD_AGREEMENT"].includes(role);
}

export function getPricingEvidenceLabel(document, page) {
  const role = document.pageAssignments?.find((assignment) => assignment.pages?.includes(page))?.role || document.role;
  if (role === "TARGET_CLAIM_REMITTANCE") return "case QPA EOB";
  if (isHistoricalRole(role)) return "Exhibit B";
  return "";
}

export function formatPricingReference(ref, manifest) {
  const document = manifest.find((item) =>
    item.id === ref.documentId && Number(item.version) === Number(ref.documentVersion));
  if (!document || (ref.page !== null && (!Number.isInteger(ref.page) || ref.page < 1))) {
    throw new Error("An analysis citation does not match the current case sources.");
  }
  const label = getPricingEvidenceLabel(document, ref.page) || "Supporting evidence";
  return `${label}, ${ref.page === null ? "page unavailable" : `page ${ref.page}`}`;
}

export function formatReportText(text, manifest) {
  return [...manifest].filter((document) => document.name)
    .sort((a, b) => b.name.length - a.name.length)
    .reduce((result, document) => result.split(document.name).join(
      getPricingEvidenceLabel(document) || "supporting evidence"
    ), String(text));
}

function formatPaymentAmount(amount, currency) {
  if (currency === "USD" && /^-?\d+(?:\.\d{1,2})?$/.test(amount) &&
      Number.isSafeInteger(Math.round(Number(amount) * 100))) {
    return new Intl.NumberFormat("en-US", { style: "currency", currency }).format(Number(amount));
  }
  return [currency, amount].filter(Boolean).join(" ") || "—";
}

export function getHistoricalPaymentRows(pricing, manifest) {
  return (pricing?.comparatorCandidates || []).filter((item) => item.sourceRefs?.some((ref) =>
    manifest.some((document) => document.id === ref.documentId &&
      Number(document.version) === Number(ref.documentVersion) &&
      getPricingEvidenceLabel(document, ref.page) === "Exhibit B")
  )).map((item) => ({
    carrier: item.insuranceCarrier || "—",
    code: item.cptHcpcs || "—",
    modifiers: item.modifiers || "—",
    date: item.serviceDate || "—",
    amount: formatPaymentAmount(item.amount, item.currency),
    basis: item.paymentBasis || "—",
    sourceRefs: item.sourceRefs,
  }));
}

export function getAnalysisReviewWarnings(clinical, pricing) {
  return [...new Set([
    ...(clinical?.missingInformation || []), ...(clinical?.riskFlags || []),
    ...(clinical?.findings || []).filter((item) => item.evidenceType !== "DOCUMENTED")
      .map((item) => `${item.evidenceType}: ${item.finding}`),
    ...(pricing?.missingInformation || []),
    ...(pricing?.serviceLineAssessments || []).flatMap((item) => item.missingEvidence || []),
    ...(pricing?.comparatorCandidates || []).flatMap((item) => item.limitations || []),
    ...(pricing?.paymentFacts || []).flatMap((item) => item.limitations || []),
    ...(pricing?.codeDiscrepancies || []).map((item) => item.details),
  ].filter(Boolean))];
}

export function buildCombinedDocument(clinical, pricing, manifest) {
  if (!clinical?.summary || !Array.isArray(clinical.findings) ||
      !pricing?.pricingSupportNarrative?.sections?.length) {
    throw new Error("Both medical analysis and a pricing-support narrative are required. Rerun older analyses that do not include the narrative.");
  }
  const citation = (refs) => [...new Set((refs || []).map((ref) => formatPricingReference(ref, manifest)))].join("; ");
  const evidence = clinical.findings.map((finding) => {
    citation(finding.sourceRefs);
    return `- ${finding.finding}\n  ${finding.relevance}`;
  });
  const sections = pricing.pricingSupportNarrative.sections.map((section) =>
    `### ${section.heading}\n\n${section.text}${section.sourceRefs?.length ? `\n\n${citation(section.sourceRefs)}` : ""}`
  );
  const cell = (value) => String(value).replace(/\|/g, "\\|").replace(/[\r\n]+/g, " ");
  const historicalPayments = getHistoricalPaymentRows(pricing, manifest);
  const table = historicalPayments.length ? [
    "### Historical Payments — Exhibit B",
    [
      "| Insurance carrier | CPT/HCPCS | Modifiers | Service date | Amount | Payment basis | Evidence |",
      "| --- | --- | --- | --- | --- | --- | --- |",
      ...historicalPayments.map((row) =>
        `| ${[row.carrier, row.code, row.modifiers, row.date, row.amount, row.basis,
          citation(row.sourceRefs)].map(cell).join(" | ")} |`),
    ].join("\n"),
  ] : [];
  const roles = manifest.flatMap((item) => [item.role, ...(item.pageAssignments || []).map((assignment) => assignment.role)]);
  return formatReportText([
    "# Combined Medical Necessity and Pricing Support",
    "## Medical Necessity Evidence Review",
    clinical.summary, ...evidence,
    `## ${pricing.pricingSupportNarrative.title}`, ...sections,
    ...table,
    "### Exhibits",
    ...(roles.includes("TARGET_CLAIM_REMITTANCE") ? ["case QPA EOB: Current case remittance."] : []),
    ...(roles.some(isHistoricalRole) ? ["Exhibit B: Historical reimbursement evidence."] : []),
    "Human verification is required. This document does not determine IDR eligibility and has not been filed or sent.",
  ].join("\n\n"), manifest);
}
